/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";

import { GetAttributesValues, StrapiPricingPlan } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { Spacing } from "@/tokens/spacing";

import { Button } from "@/ui/atoms/button";
import { Flag } from "@/ui/atoms/flag";
import { List } from "@/ui/atoms/list";
import { ListItem } from "@/ui/atoms/list_item";
import { PricingCardPopover } from "@/ui/atoms/pricing_card_popover";
import { Text } from "@/ui/atoms/text";

import { formatPrice } from "@/util/data_util";
import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { getPricingPlanCtaTrack } from "@/util/pricing_util";
import { buildStylesByBreakpoint } from "@/util/style_util";

interface PricingPlanCardProps extends StrapiPricingPlan {
    className?: SerializedStyles;
    index: number;
    isEmphasized?: boolean;
}

export function PricingPlanCard(props: PricingPlanCardProps) {
    /**
     * Globals
     */
    const theme = useTypedTheme();

    /**
     * Styles
     */
    const cardStyles = css(
        {
            backgroundColor: props.isEmphasized
                ? Colors["lighten-10"]
                : Colors[theme.backgrounds.backgroundTertiary],
            borderRadius: BorderRadiuses.borderLarge,
            display: "flex",
            flexDirection: "column",
            padding: Spacing["spacing-6"],
            rowGap: Spacing["spacing-6"],
        },
        props.className,
    );

    const cardTitleRowStyles = css({
        alignItems: "center",
        columnGap: Spacing["spacing-half"],
        display: "flex",
        justifyContent: "space-between",
        marginBottom: Spacing["spacing-3"],
    });

    const pricingDetailsStyles = css({
        display: "flex",
        flexDirection: "column",
        rowGap: Spacing["spacing-1"],
    });

    const pricingCostStyles = css({
        alignItems: "baseline",
        display: "flex",
        gap: Spacing["spacing-2"],
    });

    const emptyMonthlyPriceStyles = css(
        {
            visibility: "hidden",
        },
        buildStylesByBreakpoint("display", {
            extraSmall: "none",
            medium: "block",
        }),
    );

    const featuresListStyles = css({
        flexGrow: 1,
    });

    const featuresListTitleStyles = buildStylesByBreakpoint("marginBottom", {
        extraSmall: Spacing["spacing-3"],
        large: Spacing["spacing-4"],
    });

    const buttonStyles = css({
        justifyContent: "center",
        width: "100%",
    });

    /**
     * Elements
     */
    const renderPricingCardListItems = (
        listItems: GetAttributesValues<"components.component-pricing-list-item">[],
    ) =>
        listItems.map(({ id, Popover_Text, Text }) => {
            return (
                <ListItem
                    fontSize="PlanCardDetails"
                    iconSlug="Check Circled"
                    key={id}
                    themeKey="textEmphasis"
                >
                    {Popover_Text ? (
                        <PricingCardPopover
                            popoverContent={Popover_Text}
                            title={Text}
                        />
                    ) : (
                        Text
                    )}
                </ListItem>
            );
        });

    const renderPlanFeatures = () => {
        const { Features_List } = props;

        // This is never falsy, but just a failsafe
        if (!Features_List) {
            return;
        }

        return (
            <div css={featuresListStyles}>
                <Text
                    className={featuresListTitleStyles}
                    fontSize="PlanCardDetails"
                    tag="h3"
                    themeKey="textSecondary"
                >
                    {Features_List.Title}
                </Text>

                <List>
                    {renderPricingCardListItems(Features_List.List_Items)}
                </List>
            </div>
        );
    };

    const renderPlanPricingDetails = () => {
        return (
            <div css={pricingDetailsStyles}>
                <div css={pricingCostStyles}>
                    <Text
                        fontSize="PlanCardPrice"
                        fontWeight="medium"
                        themeKey="headlinePrimary"
                    >
                        {props.Price_Override ||
                            (props.Price_Annual &&
                                formatPrice(props.Price_Annual))}
                    </Text>

                    {props.Price_Annual && props.Price_Annual > 0 && (
                        <Text
                            fontSize="PlanCardDetails"
                            themeKey="textSecondary"
                        >
                            / user / mo, billed annually
                        </Text>
                    )}
                </div>

                {props.Price_Monthly ? (
                    <Text fontSize="PlanCardDetails" themeKey="textSecondary">
                        {formatPrice(props.Price_Monthly)} billed monthly
                    </Text>
                ) : (
                    <Text
                        ariaHidden
                        className={emptyMonthlyPriceStyles}
                        fontSize="PlanCardDetails"
                        themeKey="textSecondary"
                    >
                        n/a
                    </Text>
                )}

                {props.User_Count_Terms && (
                    <Text
                        className={css({
                            marginTop: Spacing["spacing-1"],
                        })}
                        fontSize="PlanCardDetails"
                        themeKey="textSecondary"
                    >
                        {props.User_Count_Terms}
                    </Text>
                )}
            </div>
        );
    };

    const renderPlanTitle = () => {
        return (
            <div>
                <div css={cardTitleRowStyles}>
                    <Text
                        fontSize="PlanCardTitle"
                        fontWeight="medium"
                        labelLineHeight={true}
                        tag="h2"
                        themeKey="headlinePrimary"
                    >
                        {props.Name}
                    </Text>

                    {props.Flag && <Flag>{props.Flag}</Flag>}
                </div>

                <Text fontSize="TEXT_1" themeKey="textPrimary">
                    {props.Description}
                </Text>
            </div>
        );
    };

    return (
        <div css={cardStyles}>
            {renderPlanTitle()}

            {renderPlanPricingDetails()}

            <Button
                className={buttonStyles}
                fontSize="CtaHero"
                href={props.CTA.URL}
                track={getPricingPlanCtaTrack(props)}
                variant={props.isEmphasized ? "primary" : "secondary"}
            >
                {props.CTA.Text}
            </Button>

            {renderPlanFeatures()}
        </div>
    );
}
